import { IAccessRulesBlock } from './types';

/**
 * A store row / payload fragment that may carry access-rule metadata.
 *
 * Unlike IWithAccessRules (a loose read-side view for extract.ts), this is the
 * WRITE-side contract used when copying the metadata between a payload and a
 * store row.
 */
export interface IAccessRulesCarrier {
  '@accessRules'?: IAccessRulesBlock;
  virtual?: {
    '@accessRules'?: IAccessRulesBlock;
    [k: string]: unknown;
  };
}

/**
 * Deep-copy via JSON so store slices never share one mutable rules graph.
 * Falls back to the original reference on non-JSON-safe input (circular etc.)
 * — sharing is safer than dropping a restriction block.
 */
const jsonClone = <T>(value: T): T => {
  try {
    return JSON.parse(JSON.stringify(value)) as T;
  } catch {
    return value;
  }
};

/**
 * Copy the '@accessRules' / 'virtual' carriers from an incoming payload onto a
 * store row, honouring the backend contract (src/accessControl/types.ts):
 * an ABSENT key means "no restrictions".
 *
 * - key present on source → deep-copied (JSON clone) onto target;
 * - key absent on source  → DELETED from target. Keeping a stale block would
 *   freeze old restrictions on the row after the user's rights widen.
 */
export const copyAccessRuleCarriers = (
  target: IAccessRulesCarrier,
  source: IAccessRulesCarrier | null | undefined,
): void => {
  const src: IAccessRulesCarrier | undefined = (
    source && typeof source === 'object' ? source : undefined
  );

  const rules = src?.['@accessRules'];
  if (rules !== undefined && rules !== null) {
    target['@accessRules'] = jsonClone(rules);
  } else {
    delete target['@accessRules'];
  }

  const virtual = src?.virtual;
  if (virtual !== undefined && virtual !== null) {
    target.virtual = jsonClone(virtual);
  } else {
    delete target.virtual;
  }
};
